import { useEffect, useRef } from 'react';
import { useTracks } from '@livekit/components-react';
import { toast } from 'react-toastify';
import { Track, TrackEvent } from 'livekit-client';

import { useJukeboxVolumeStore } from '../../model/jukebox-volume.store';
import { heardBeatCalibrator } from '../../lib/heard-beat-calibrator';
import { SYNC_MIX_TRACK_NAME } from '../audio-controls-provider';

/**
 * Скрытый <audio> для одного remote-трека с собственным управлением громкостью.
 *
 * Свой элемент вместо <AudioTrack volume={...}> из components-react:
 * тот применяет громкость через RemoteAudioTrack.setVolume, а livekit-client
 * при повторном attach восстанавливает её только из this.elementVolume
 * (проверка `if (this.elementVolume)` — ноль falsy, поэтому «выключенная»
 * музыка слетала на 100% после рестарта трека собеседником, переоткрытия
 * полноэкранного режима или ресайза). Здесь громкость выставляется
 * напрямую на элемент и переустанавливается на КАЖДОМ рендере, поэтому
 * любой ре-аттач/пересоздание тут же возвращает актуальное значение.
 */
const RemoteAudioElement = ({ trackRef, trackName, volume }) => {
  const participantIdentity = trackRef.participant?.identity;
  const audioRef = useRef(null);
  const track = trackRef.publication?.track;

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !track) return undefined;
    track.attach(el);
    return () => {
      track.detach(el);
    };
  }, [track]);

  // Safari/iOS блокирует play() у элементов, приаттаченных без «свежего» жеста
  // пользователя (типично: трек sync-mix публикуется спустя секунды после
  // входа в комнату). LiveKit при этом шлёт AudioPlaybackFailed — без
  // обработки трек молчит незаметно для пользователя. Показываем подсказку
  // и повторяем play() по первому клику в любом месте страницы.
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !track || typeof track.on !== 'function') return undefined;
    let retryRef = null;
    const onPlaybackFailed = () => {
      toast.error('Браузер заблокировал звук собеседника. Кликните в любом месте страницы, чтобы включить звук.', {
        toastId: `audio-playback-blocked-${trackName}`,
        autoClose: 10000,
      });
      const retry = () => {
        document.removeEventListener('click', retry);
        retryRef = null;
        el.play().catch(() => {});
      };
      retryRef = retry;
      document.addEventListener('click', retry);
    };
    track.on(TrackEvent.AudioPlaybackFailed, onPlaybackFailed);
    return () => {
      track.off(TrackEvent.AudioPlaybackFailed, onPlaybackFailed);
      if (retryRef) document.removeEventListener('click', retryRef);
    };
  }, [track, trackName]);

  // Без массива зависимостей: эффект выполняется после каждого рендера,
  // значит после любого attach/re-attach/обновления стора громкость
  // немедленно возвращается к значению из jukebox-volume.store.
  useEffect(() => {
    const el = audioRef.current;
    if (!el || volume === undefined) return;
    el.volume = volume;
    if (typeof track?.setVolume === 'function') {
      // Держим elementVolume livekit-client в синхроне, чтобы его
      // собственные ре-аттачи тоже применяли наше значение.
      track.setVolume(volume);
    }
  });

  // Тап удалённого микса в калибровщик визуала: по слышимым кликам метронома
  // он подстраивает фазу долей под реальную задержку звука (сеть + джиттер).
  // На воспроизведение не влияет — тап параллельный.
  useEffect(() => {
    if (trackName !== SYNC_MIX_TRACK_NAME || !track) return undefined;
    const key = trackRef.publication.trackSid;
    heardBeatCalibrator.tapTrack(track, key, trackRef.participant.identity);
    return () => {
      heardBeatCalibrator.untap(key);
    };
  }, [track, trackName, trackRef]);

  return <audio ref={audioRef} data-jukebox-volume={volume} />;
};

/**
 * Аналог RoomAudioRenderer, но треки синхронного аудио рендерятся
 * с собственной громкостью, независимо от микрофона собеседника:
 *  - 'jukebox' — музыка из плеера (musicVolume);
 *  - 'metronome' — метроном (metronomeVolume).
 * Громкость хранится в jukebox-volume.store (localStorage) и переживает
 * перезаход в комнату — у каждого пользователя своя.
 */
export const AppointmentAudioRenderer = () => {
  const { musicVolume, metronomeVolume } = useJukeboxVolumeStore();

  const trackRefs = useTracks(
    [Track.Source.Microphone, Track.Source.ScreenShareAudio, Track.Source.Unknown],
    { updateOnlyOn: [], onlySubscribed: true }
  ).filter(
    (trackRef) =>
      !trackRef.participant.isLocal && trackRef.publication.kind === Track.Kind.Audio
  );

  return (
    <div style={{ display: 'none' }}>
      {trackRefs.map((trackRef) => {
        const trackName = trackRef.publication.trackName;
        const volume =
          trackName === 'jukebox'
            ? musicVolume
            : trackName === 'metronome'
              ? metronomeVolume
              : undefined;

        return (
          <RemoteAudioElement
            key={`${trackRef.participant.identity}-${trackRef.publication.trackSid}`}
            trackRef={trackRef}
            trackName={trackName}
            volume={volume}
          />
        );
      })}
    </div>
  );
};
